import type { Price } from 'domain/listing/value-objects';
import type { SupportedCurrency } from 'utils/i18n-format-currency';

export interface FormatPriceOptions {
  currency: SupportedCurrency;
  locale: string;
  compact?: boolean;
}

const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  USD: '$',
  CAD: '$',
};

/**
 * Formats a Price VO for display.
 * 
 * @param price - Price VO (use Price.from() to create)
 * @param options - Currency and locale settings
 * @returns Formatted string or null if price is not displayable
 */
export function formatPrice(
  price: Price,
  options: FormatPriceOptions
): string | null {
  if (!price.isPositive) {
    return null;
  }

  const amount = price.value!; // Safe: isPositive guarantees non-null and > 0
  const { currency, locale, compact = false } = options;
  const symbol = CURRENCY_SYMBOLS[currency];

  if (compact) {
    const needsDecimals = 
      (amount % 1000 !== 0 && amount < 1000000) ||
      (amount % 1000000 !== 0 && amount >= 1000000);

    const formatted = new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: needsDecimals ? 1 : 0,
    }).format(amount);

    return `${symbol}${formatted}`;
  }

  const formatted = amount.toLocaleString(locale, { maximumFractionDigits: 0 });
  return `${symbol}${formatted}`;
}

export const CURRENCY_SYMBOL = CURRENCY_SYMBOLS;
